var { useState, useEffect, useRef, useCallback } = require('react');
var {
  getPointOnPath,
  getAngleOnPath,
  generateRandomPath,
} = require('./useBezierPath');
var {
  ANIMATION_DURATION_MIN,
  ANIMATION_DURATION_MAX,
  FADE_OUT_DURATION,
  AUTO_DISMISS_DELAY,
  UNICORN_SIZE,
  STAMPEDE_MIN,
  STAMPEDE_MAX,
} = require('./UnicornCelebration.types');

/**
 * Check if user prefers reduced motion
 * @returns {boolean}
 */
function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Generate a random duration between min and max
 * @returns {number} - Duration in ms
 */
function getRandomDuration() {
  return (
    ANIMATION_DURATION_MIN +
    Math.random() * (ANIMATION_DURATION_MAX - ANIMATION_DURATION_MIN)
  );
}

/**
 * Generate unicorn configurations
 * @param {boolean} isStampede - Whether this is a stampede (multiple unicorns)
 * @param {number} screenWidth - Screen width
 * @param {number} screenHeight - Screen height
 * @returns {Array} - Array of unicorn configurations
 */
function generateUnicornConfigs(isStampede, screenWidth, screenHeight) {
  var count = isStampede
    ? STAMPEDE_MIN +
      Math.floor(Math.random() * (STAMPEDE_MAX - STAMPEDE_MIN + 1))
    : 1;

  var configs = [];

  for (var i = 0; i < count; i++) {
    configs.push({
      id: i,
      path: generateRandomPath(screenWidth, screenHeight),
      duration: getRandomDuration(),
      delay: isStampede ? i * 150 + Math.random() * 100 : 0, // Stagger stampede
      size: UNICORN_SIZE * (0.8 + Math.random() * 0.4), // Slight size variation
    });
  }

  return configs;
}

/**
 * Determine if celebration should be solo or stampede (50/50)
 * @returns {boolean} - true for stampede, false for solo
 */
function shouldBeStampede() {
  return Math.random() >= 0.5;
}

/**
 * Custom hook for managing unicorn animation state
 * @param {Object} unicornConfig - Configuration for this unicorn
 * @param {boolean} isActive - Whether animation is active
 * @returns {Object} - Current position, angle, and progress
 */
function useUnicornAnimation(unicornConfig, isActive) {
  var positionState = useState({ x: 0, y: 0 });
  var position = positionState[0];
  var setPosition = positionState[1];

  var angleState = useState(0);
  var angle = angleState[0];
  var setAngle = angleState[1];

  var progressState = useState(0);
  var progress = progressState[0];
  var setProgress = progressState[1];

  var isCompleteState = useState(false);
  var isComplete = isCompleteState[0];
  var setIsComplete = isCompleteState[1];

  var animationRef = useRef(null);
  var startTimeRef = useRef(null);

  var animate = useCallback(
    function (timestamp) {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      var elapsed = timestamp - startTimeRef.current - unicornConfig.delay;

      // Handle delay
      if (elapsed < 0) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      var t = Math.min(elapsed / unicornConfig.duration, 1);
      setProgress(t);

      var newPosition = getPointOnPath(unicornConfig.path, t);
      var newAngle = getAngleOnPath(unicornConfig.path, t);

      setPosition(newPosition);
      setAngle(newAngle);

      if (t < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setIsComplete(true);
      }
    },
    [unicornConfig],
  );

  useEffect(
    function () {
      if (isActive && !prefersReducedMotion()) {
        animationRef.current = requestAnimationFrame(animate);
      }

      return function () {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    },
    [isActive, animate],
  );

  // Reset when config changes
  useEffect(
    function () {
      setIsComplete(false);
      setProgress(0);
      startTimeRef.current = null;
    },
    [unicornConfig.id],
  );

  return {
    position: position,
    angle: angle,
    progress: progress,
    isComplete: isComplete,
    prefersReducedMotion: prefersReducedMotion(),
  };
}

/**
 * Custom hook for managing the overall celebration state
 * @returns {Object} - Celebration state and controls
 */
function useCelebrationState() {
  var isActiveState = useState(false);
  var isActive = isActiveState[0];
  var setIsActive = isActiveState[1];

  var isFadingOutState = useState(false);
  var isFadingOut = isFadingOutState[0];
  var setIsFadingOut = isFadingOutState[1];

  var unicornsState = useState([]);
  var unicorns = unicornsState[0];
  var setUnicorns = unicornsState[1];

  var modeState = useState(null); // 'solo' or 'stampede'
  var mode = modeState[0];
  var setMode = modeState[1];

  var dismiss = useCallback(function () {
    setIsFadingOut(true);
    setTimeout(function () {
      setIsActive(false);
      setIsFadingOut(false);
      setUnicorns([]);
      setMode(null);
    }, FADE_OUT_DURATION);
  }, []);

  var startCelebration = useCallback(function () {
    if (prefersReducedMotion()) {
      return; // Don't show animation for users who prefer reduced motion
    }

    if (typeof window === 'undefined') {
      return; // Can't show animation without window
    }

    var screenWidth = window.innerWidth;
    var screenHeight = window.innerHeight;
    var isStampede = shouldBeStampede();

    setMode(isStampede ? 'stampede' : 'solo');
    setUnicorns(generateUnicornConfigs(isStampede, screenWidth, screenHeight));
    setIsActive(true);
    setIsFadingOut(false);
  }, []);

  // Auto-dismiss when all unicorns complete
  var handleUnicornComplete = useCallback(
    function (unicornId) {
      setUnicorns(function (prev) {
        var updated = prev.map(function (u) {
          return u.id === unicornId
            ? Object.assign({}, u, { completed: true })
            : u;
        });

        // Check if all unicorns are complete
        var allComplete = updated.every(function (u) {
          return u.completed;
        });
        if (allComplete) {
          setTimeout(dismiss, AUTO_DISMISS_DELAY);
        }

        return updated;
      });
    },
    [dismiss],
  );

  return {
    isActive: isActive,
    isFadingOut: isFadingOut,
    unicorns: unicorns,
    mode: mode,
    startCelebration: startCelebration,
    dismiss: dismiss,
    handleUnicornComplete: handleUnicornComplete,
  };
}

module.exports = {
  prefersReducedMotion: prefersReducedMotion,
  generateUnicornConfigs: generateUnicornConfigs,
  shouldBeStampede: shouldBeStampede,
  useUnicornAnimation: useUnicornAnimation,
  useCelebrationState: useCelebrationState,
};
